import React from "react";
import PropTypes from "prop-types";
import { PortableText } from "@portabletext/react";
import { urlFor } from "../../services/sanityQueries";

const components = {
  types: {
    image: ({ value }) => {
      if (!value?.asset) return null;
      return (
        <figure className="post-inline-image">
          <img
            src={urlFor(value).width(800).auto("format").url()}
            alt={value.alt || ""}
            loading="lazy"
          />
          {value.alt && <figcaption>{value.alt}</figcaption>}
        </figure>
      );
    },
  },
  block: {
    h2: ({ children, value }) => <h2 id={headingId(value)}>{children}</h2>,
    h3: ({ children, value }) => <h3 id={headingId(value)}>{children}</h3>,
    normal: ({ children }) => <p>{children}</p>,
    blockquote: ({ children }) => <blockquote>{children}</blockquote>,
  },
  marks: {
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    link: ({ value, children }) => {
      // Whitelist safe URL schemes — block javascript: and data: XSS vectors
      const href = sanitizeHref(value?.href);
      if (!href) return <>{children}</>;
      const rel    = value?.blank ? "noopener noreferrer" : undefined;
      const target = value?.blank ? "_blank" : undefined;
      return (
        <a href={href} target={target} rel={rel}>
          {children}
        </a>
      );
    },
  },
};

export default function PortableTextRenderer({ value }) {
  if (!value) return null;
  return (
    <div className="portable-text">
      <PortableText value={value} components={components} />
    </div>
  );
}

PortableTextRenderer.propTypes = {
  value: PropTypes.arrayOf(PropTypes.object),
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Generate a stable ID from a Portable Text block — mirrors slugifyHeading in sanityNormalizer. */
function headingId(block) {
  const text = (block?.children || []).map((c) => c.text || "").join("");
  return text
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}-]/gu, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Allow only safe URL schemes. Returns null for unsafe or empty hrefs. */
function sanitizeHref(href) {
  if (!href || typeof href !== "string") return null;
  const trimmed = href.trim().toLowerCase();
  if (
    trimmed.startsWith("https://") ||
    trimmed.startsWith("http://") ||
    trimmed.startsWith("mailto:")
  ) {
    return href.trim();
  }
  return null;
}
